import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge, dt } from "../components/common";
import { Modal, Field, TextArea, Selecter } from "./crm/crmui";

export default function Workflows() {
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [catalog, setCatalog] = useState({ eventos: [], acoes: [] });
  const [show, setShow] = useState(false);
  const [rule, setRule] = useState({ nome: "", evento: "crm.lead.created", acaoTipo: "criar_tarefa", params: {} });

  const load = async () => {
    try {
      const [w, r, c] = await Promise.all([api.get("/workflows"), api.get("/workflows/runs"), api.get("/workflows/events-catalog")]);
      setRules(w.data); setRuns(r.data); setCatalog(c.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    if (!rule.nome.trim()) return toast.error("Indique o nome.");
    try {
      await api.post("/workflows", {
        nome: rule.nome, evento: rule.evento, condicoes: [],
        acoes: [{ tipo: rule.acaoTipo, params: rule.params }], ativo: true,
      });
      toast.success("Regra criada."); setShow(false);
      setRule({ nome: "", evento: "crm.lead.created", acaoTipo: "criar_tarefa", params: {} });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggle = async (r) => {
    try { await api.put(`/workflows/${r.id}`, { ativo: !r.ativo }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/workflows/${id}`); toast.success("Regra removida."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setP = (k, v) => setRule({ ...rule, params: { ...rule.params, [k]: v } });

  const cols = [
    { key: "nome", label: "Nome", render: (r) => <span className="font-semibold text-navy">{r.nome}</span> },
    { key: "evento", label: "Evento" },
    { key: "acoes", label: "Acoes", render: (r) => (r.acoes || []).map((a) => a.tipo).join(", ") },
    { key: "execucoes", label: "Execucoes", render: (r) => r.execucoes || 0 },
    { key: "ativo", label: "Ativo", render: (r) => <button onClick={() => toggle(r)} data-testid={`toggle-wf-${r.id}`}><Badge value={r.ativo ? "ativo" : "nao_configurado"} /></button> },
    { key: "acoes2", label: "", render: (r) => <button onClick={() => remove(r.id)} data-testid={`del-wf-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={15} /></button> },
  ];
  const runCols = [
    { key: "rule_nome", label: "Regra", render: (r) => <span className="font-semibold text-navy">{r.rule_nome}</span> },
    { key: "evento", label: "Evento" },
    { key: "resultados", label: "Resultado", render: (r) => (r.resultados || []).join(", ") },
    { key: "em", label: "Quando", render: (r) => dt(r.em) },
  ];

  return (
    <>
      <PageHeader overline="Automacao" title="Motor de workflows" action={<Btn onClick={() => setShow(true)} testid="new-workflow-btn"><Plus size={16} /> Nova regra</Btn>} />
      <p className="text-sm text-muted2 mb-4 max-w-2xl">Quando ocorre um evento na plataforma (ex.: um novo lead), a regra executa acoes automaticamente - criar tarefa, registar atividade ou enviar email. Use variaveis do evento com chavetas, ex.: {"{{plataforma}}"}.</p>
      <Table testid="workflows-table" columns={cols} rows={rules} />

      <h2 className="text-lg font-bold text-navy mt-8 mb-3">Ultimas execucoes</h2>
      <Table testid="runs-table" columns={runCols} rows={runs} />

      <Modal open={show} onClose={() => setShow(false)} title="Nova regra de automacao" testid="workflow-modal">
        <Field label="Nome" value={rule.nome} onChange={(v) => setRule({ ...rule, nome: v })} testid="wf-nome" />
        <Selecter label="Quando ocorrer o evento" value={rule.evento} onChange={(v) => setRule({ ...rule, evento: v })} options={(catalog.eventos || []).map((e) => ({ value: e, label: e }))} testid="wf-evento" />
        <Selecter label="Executar acao" value={rule.acaoTipo} onChange={(v) => setRule({ ...rule, acaoTipo: v, params: {} })} options={(catalog.acoes || []).map((a) => ({ value: a, label: a }))} testid="wf-acao" />
        {rule.acaoTipo === "criar_tarefa" && (
          <>
            <Field label="Titulo da tarefa" value={rule.params.titulo || ""} onChange={(v) => setP("titulo", v)} placeholder="Contactar lead de {{plataforma}}" testid="wf-task-titulo" />
            <Field label="Prazo (dias)" type="number" value={rule.params.prazo_dias || ""} onChange={(v) => setP("prazo_dias", v)} testid="wf-task-prazo" />
            <Selecter label="Prioridade" value={rule.params.prioridade || "media"} onChange={(v) => setP("prioridade", v)} options={["baixa", "media", "alta"]} testid="wf-task-prioridade" />
          </>
        )}
        {rule.acaoTipo === "registar_atividade" && (
          <>
            <Field label="Assunto" value={rule.params.assunto || ""} onChange={(v) => setP("assunto", v)} testid="wf-act-assunto" />
            <TextArea label="Corpo" value={rule.params.corpo || ""} onChange={(v) => setP("corpo", v)} testid="wf-act-corpo" />
          </>
        )}
        {rule.acaoTipo === "enviar_email" && (
          <>
            <Field label="Para (email ou payload.email)" value={rule.params.para || ""} onChange={(v) => setP("para", v)} placeholder="payload.email" testid="wf-email-para" />
            <Field label="Assunto" value={rule.params.assunto || ""} onChange={(v) => setP("assunto", v)} testid="wf-email-assunto" />
            <Field label="Chave de template (opcional)" value={rule.params.template_key || ""} onChange={(v) => setP("template_key", v)} testid="wf-email-template" />
            <TextArea label="HTML (se sem template)" value={rule.params.html || ""} onChange={(v) => setP("html", v)} testid="wf-email-html" />
          </>
        )}
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancelar</Btn><Btn onClick={create} testid="wf-save">Criar regra</Btn></div>
      </Modal>
    </>
  );
}
